import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { SPACING } from "../theme/constants";
import {
  Box,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  Snackbar,
} from "@mui/material";
import { FileCopy } from "@mui/icons-material";
import {
  updateSite,
  restoreSite,
  cloneSite,
  fetchSiteById,
} from "../redux/slices/siteSlice";
import {
  fetchSiteBackups,
  createNewBackup,
  restoreSiteBackup,
  deleteSiteBackup,
  downloadSiteBackup,
} from "../redux/slices/backupSlice";
import {
  addSftpUser,
  removeSftpUser,
  fetchSftpUsers,
} from "../redux/slices/sftpSlice";
import SiteSettingsDialog from "../components/dialog/SiteSettingsDialog";
import CreateBackupDialog from "../components/dialog/CreateBackupDialog";
import CloneSiteDialog from "../components/dialog/CloneSiteDialog";
import { SoftDeleteBanner } from "../components/SiteDetailExtras";
import QuickActionsPanel from "../components/QuickActionsPanel";
import {
  SiteHeader,
  SiteStats,
  SiteOverviewTab,
  SitePerformanceTab,
  SitePluginsTab,
  SiteSettingsTab,
  SiteBackupsTab,
  SiteSftpTab,
  ManageBackupDialog,
  SiteGitTab,
  SiteDeploymentsTab,
  SiteMigrationsTab,
} from "../components/siteDetail";
import ApmTab from "../components/ApmTab";
import EnvironmentSwitcher from "../components/siteDetail/EnvironmentSwitcher";
import { useSiteDetail } from "../hooks/useSiteDetail";

const SiteDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();

  // Tab name to index mapping
  const tabMapping = {
    overview: 0,
    performance: 1,
    apm: 2,
    plugins: 3,
    settings: 4,
    sftp: 5,
    backups: 6,
    git: 7,
    deployments: 8,
    migrations: 9,
  };

  // Get initial tab from URL query parameter
  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab")?.toLowerCase();
    return tabMapping[tabParam] ?? 0;
  };

  // Component state
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [siteMenuAnchor, setSiteMenuAnchor] = useState(null);
  const [selectedBackupForAction, setSelectedBackupForAction] = useState(null);
  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(getInitialTab());
  const [successMessage, setSuccessMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false);

  const showMessage = (msg, type = "success") => {
    setSuccessMessage(msg);
    setMessageType(type);
  };

  // Update tab when URL changes
  useEffect(() => {
    setTabValue(getInitialTab());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Redux state
  const { singleSite, loading } = useSelector((state) => state.site);
  const { user } = useSelector((state) => state.user);
  const sftpUsers = useSelector((state) => state.sftp);
  const isVerifiedOrAdmin = user?.emailVerified || user?.role === "ADMIN";
  const VERIFY_MSG =
    "Please verify your email address before performing this action.";
  const { backups, createLoading, deleteLoading, downloadLoading } =
    useSelector((state) => state.backup);

  // Custom hook for data fetching and socket events
  useSiteDetail({
    siteId: id,
    onShowMessage: showMessage,
  });

  // Handler functions
  const handleCreateBackup = async (siteId, payload) => {
    if (!isVerifiedOrAdmin) {
      showMessage(VERIFY_MSG, "error");
      return;
    }
    try {
      await dispatch(createNewBackup({ siteId, payload })).unwrap();
      showMessage("Backup started successfully!");
      setTimeout(() => {
        dispatch(fetchSiteBackups({ siteId: id }));
      }, 2000);
    } catch (error) {
      throw new Error(error?.payload?.error || "Failed to create backup");
    }
  };

  const handleRestoreSite = async () => {
    if (!singleSite?.site?.id) return;
    setRestoreLoading(true);
    try {
      await dispatch(restoreSite(id)).unwrap();
      showMessage("Site restored successfully!");
      setTimeout(() => {
        dispatch(fetchSiteBackups({ siteId: id }));
      }, 1000);
    } catch (error) {
      showMessage(
        `Failed to restore site: ${error.error || error.message}`,
        "error",
      );
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleUpdateSite = async (siteId, payload) => {
    try {
      const result = await dispatch(updateSite({ siteId, payload })).unwrap();
      dispatch(fetchSiteBackups({ siteId: id }));
      setSettingsDialogOpen(false);
      return result;
    } catch (error) {
      showMessage(
        `Update failed: ${error?.payload?.error || error?.message || "Unknown error"}`,
        "error",
      );
      throw new Error(
        error?.payload?.error || error?.message || "Failed to update site",
      );
    }
  };

  const handleSiteMenuOpen = (event) => {
    setSiteMenuAnchor(event.currentTarget);
  };

  const handleSiteMenuClose = () => {
    setSiteMenuAnchor(null);
  };

  const handleOpenSettings = () => {
    setSettingsDialogOpen(true);
    handleSiteMenuClose();
  };

  const handleCreateUser = async (payload) => {
    if (!isVerifiedOrAdmin) {
      showMessage(VERIFY_MSG, "error");
      throw new Error(VERIFY_MSG);
    }
    try {
      await dispatch(addSftpUser(payload)).unwrap();
      dispatch(fetchSftpUsers(id));
      showMessage("SFTP user created successfully!");
    } catch (error) {
      showMessage(
        `Failed to create SFTP user: ${error.message || error}`,
        "error",
      );
      throw error;
    }
  };

  const handleDeleteUser = async ({ siteId, username }) => {
    if (!isVerifiedOrAdmin) {
      showMessage(VERIFY_MSG, "error");
      throw new Error(VERIFY_MSG);
    }
    try {
      await dispatch(removeSftpUser({ siteId, username })).unwrap();
      dispatch(fetchSftpUsers(id));
      showMessage("SFTP user deleted successfully!");
    } catch (error) {
      showMessage(
        `Failed to delete SFTP user: ${error.message || error}`,
        "error",
      );
      throw error;
    }
  };

  const handleDeleteBackup = async () => {
    if (!selectedBackupForAction) return;
    try {
      await dispatch(deleteSiteBackup(selectedBackupForAction.id)).unwrap();
      showMessage("Backup deleted successfully!");
      dispatch(fetchSiteBackups({ siteId: id }));
    } catch (error) {
      showMessage(`Failed to delete backup: ${error.message}`, "error");
    }
  };

  const handleQuickRestore = async (restoreOptions) => {
    if (!selectedBackupForAction) return;
    try {
      await dispatch(
        restoreSiteBackup({
          backupId: selectedBackupForAction.id,
          restoreFiles: restoreOptions?.restoreFiles ?? true,
          restoreDatabase: restoreOptions?.restoreDatabase ?? true,
        }),
      ).unwrap();
      showMessage("Restore process started successfully!");
    } catch (error) {
      showMessage(`Failed to start restore: ${error.message}`, "error");
    }
  };

  const handleOpenManageDialog = (backup) => {
    setSelectedBackupForAction(backup);
    setIsManageDialogOpen(true);
  };

  const handleCloseManageDialog = () => {
    setIsManageDialogOpen(false);
  };

  const handleDialogDownload = async (options, method) => {
    if (!selectedBackupForAction) return;
    const backupId = selectedBackupForAction.id;

    const selectedTypes = [];
    if (options.files) selectedTypes.push("files");
    if (options.database) selectedTypes.push("database");
    if (options.metadata) selectedTypes.push("metadata");

    if (selectedTypes.length === 0) {
      showMessage("Please select at least one component to download.", "error");
      return;
    }

    showMessage(
      `Starting download of ${selectedTypes.length} component(s)...`,
      "info",
    );

    try {
      for (const fileType of selectedTypes) {
        await dispatch(downloadSiteBackup({ backupId, fileType })).unwrap();
      }
      showMessage("Download(s) completed successfully.");
    } catch (error) {
      showMessage(`Download failed: ${error?.message || error}`, "error");
    }
  };

  const handleOpenCloneDialog = () => {
    if (!isVerifiedOrAdmin) {
      showMessage(VERIFY_MSG, "error");
      return;
    }
    setCloneDialogOpen(true);
    handleSiteMenuClose();
  };

  const handleCloneSite = async (payload) => {
    try {
      const result = await dispatch(
        cloneSite({ siteId: id, payload }),
      ).unwrap();
      showMessage("Site cloning started successfully!");
      setCloneDialogOpen(false);
      return result;
    } catch (error) {
      throw new Error(
        error?.payload?.error || error?.message || "Failed to clone site",
      );
    }
  };

  const TabPanel = ({ children, value, index }) => (
    <div
      hidden={value !== index}
      style={{ overflow: "hidden", maxWidth: "100%" }}
    >
      {value === index && (
        <Box
          sx={{
            pt: { xs: 1, sm: 3 },
            px: { xs: 1, sm: 2 },
            overflow: "hidden",
            maxWidth: "100%",
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );

  if (loading || !singleSite) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        overflowX: "hidden",
        maxWidth: "100%",
        width: "100%",
        px: { xs: 0.5, sm: 0 },
      }}
    >
      <SoftDeleteBanner
        site={singleSite?.site}
        onRestore={handleRestoreSite}
        loading={restoreLoading}
      />

      {/* Header */}
      <SiteHeader
        site={singleSite?.site}
        onSettingsClick={handleOpenSettings}
        onBackupClick={() => setBackupDialogOpen(true)}
        onMoreClick={handleSiteMenuOpen}
        createLoading={createLoading}
      />

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {/* Quick Stats */}
      <SiteStats site={singleSite?.site} />

      {/* Environment Switcher */}
      {singleSite?.site?.siblingEnvironments?.length > 0 && (
        <EnvironmentSwitcher
          currentSite={singleSite.site}
          siblings={singleSite.site.siblingEnvironments}
        />
      )}

      {/* Tabs */}
      <Box
        sx={{
          mt: 3,
          maxWidth: "100%",
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label="Overview" />
            <Tab label="Performance" />
            <Tab label="APM" />
            <Tab label="Plugins" />
            <Tab label="Settings" />
            <Tab label="SFTP" />
            <Tab label="Backups" />
            <Tab label="Git & Deploy" />
            <Tab label="Deployments" />
            <Tab label="Migrations" />
          </Tabs>
        </Box>

        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <SiteOverviewTab
            site={singleSite?.site}
            onBackupClick={() => setBackupDialogOpen(true)}
            onShowMessage={showMessage}
            createLoading={createLoading}
            onUpdateSite={handleUpdateSite}
            onNavigateToSettings={() => setTabValue(4)}
          />
        </TabPanel>

        {/* Performance Tab */}
        <TabPanel value={tabValue} index={1}>
          <SitePerformanceTab />
        </TabPanel>

        {/* APM Tab */}
        <TabPanel value={tabValue} index={2}>
          <ApmTab
            targetType="site"
            targetId={id}
            targetName={
              singleSite?.site?.name || singleSite?.site?.domain || "Site"
            }
          />
        </TabPanel>

        {/* Plugins Tab */}
        <TabPanel value={tabValue} index={3}>
          <SitePluginsTab site={singleSite?.site} />
        </TabPanel>

        {/* Settings Tab */}
        <TabPanel value={tabValue} index={4}>
          <SiteSettingsTab
            site={singleSite?.site}
            siteId={id}
            onUpdateSite={handleUpdateSite}
            onShowMessage={showMessage}
            onRefreshSite={() => dispatch(fetchSiteById(id))}
          />
        </TabPanel>

        {/* SFTP Tab */}
        <TabPanel value={tabValue} index={5}>
          <SiteSftpTab
            site={singleSite?.site}
            siteId={id}
            sftpUsers={sftpUsers}
            onCreateUser={handleCreateUser}
            onDeleteUser={handleDeleteUser}
            loading={sftpUsers?.loading}
          />
        </TabPanel>

        {/* Backups Tab */}
        <TabPanel value={tabValue} index={6}>
          <SiteBackupsTab
            siteId={id}
            backups={backups}
            createLoading={createLoading}
            onCreateBackup={() => setBackupDialogOpen(true)}
            onManageBackup={handleOpenManageDialog}
          />
        </TabPanel>

        {/* Git Tab */}
        <TabPanel value={tabValue} index={7}>
          <SiteGitTab siteId={id} />
        </TabPanel>

        {/* Deployments Tab */}
        <TabPanel value={tabValue} index={8}>
          <SiteDeploymentsTab siteId={id} />
        </TabPanel>

        {/* Migrations Tab */}
        <TabPanel value={tabValue} index={9}>
          <SiteMigrationsTab siteId={id} />
        </TabPanel>
      </Box>

      {/* Quick Actions Panel */}
      <Box sx={{ mt: SPACING.lg }}>
        <QuickActionsPanel site={singleSite?.site} siteId={id} />
      </Box>

      {/* Site Action Menu */}
      <Menu
        anchorEl={siteMenuAnchor}
        open={Boolean(siteMenuAnchor)}
        onClose={handleSiteMenuClose}
      >
        <MenuItem onClick={handleOpenCloneDialog}>
          <FileCopy sx={{ mr: 1, fontSize: 20 }} />
          Clone Site
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <SiteSettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        site={singleSite?.site}
        onUpdate={handleUpdateSite}
      />

      <CreateBackupDialog
        open={backupDialogOpen}
        onClose={() => setBackupDialogOpen(false)}
        site={singleSite?.site}
        onCreate={handleCreateBackup}
      />

      <CloneSiteDialog
        open={cloneDialogOpen}
        onClose={() => setCloneDialogOpen(false)}
        site={singleSite?.site}
        onClone={handleCloneSite}
      />

      <ManageBackupDialog
        open={isManageDialogOpen}
        onClose={handleCloseManageDialog}
        backup={selectedBackupForAction}
        onRestore={handleQuickRestore}
        onDelete={handleDeleteBackup}
        onDownload={handleDialogDownload}
        restoreLoading={restoreLoading}
        deleteLoading={deleteLoading}
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={messageType} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Download Progress Indicator */}
      <Snackbar
        open={downloadLoading}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="info"
          icon={<CircularProgress size={20} />}
          sx={{ width: "100%" }}
        >
          Downloading backup file... Please wait.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SiteDetail;
